import { Icon20AddCircleOutline, Icon20CloudArrowUpOutline } from '@vkontakte/icons';
import { downloadAsJpeg } from 'Cloud/Application/DownloadAsJpeg';
import React from 'react';
import { getDownloadItemSize } from 'reactApp/appHelpers/appHelpers';
import { toolbarActions } from 'reactApp/appHelpers/toolbarActions';
import { CreatorsDescRecord } from 'reactApp/modules/editor/editor.types';
import { PublicFile } from 'reactApp/modules/public/public.types';
import { EViewMode } from 'reactApp/modules/settings/settings.types';
import { ESortOder, ESortTypes } from 'reactApp/modules/sort/sort.types';
import { CloudItem, EActions } from 'reactApp/modules/storage/storage.types';
import { DropdownTheme, ListItem } from 'reactApp/ui/DropdownList/DropdownList';
import { EColor, FavoriteIcon } from 'reactApp/ui/FavoriteIcon/FavoriteIcon';
import { TToolbarDropdownItemsKeys, TToolbarItemsKeys } from 'reactApp/ui/Toolbar/Toolbar.config';
import { EDropdownPosition, EToolbarItemTooltip, TToolbarItem } from 'reactApp/ui/Toolbar/Toolbar.types';
import {
    AddToAlbum,
    CalendarIcon,
    CheckCircleIcon,
    CheckCircleOutlineIcon,
    CompareIcon,
    CopyIcon,
    DownloadIcon,
    FileAddIcon,
    FolderAddIcon,
    HistoryIcon,
    LinkIcon,
    MailIcon,
    MoreHorizontalIcon,
    MoveIcon,
    RenameIcon,
    RestoreIcon,
    ShareAlbumIcon,
    ShareIcon,
    SortIcon,
    TextIcon,
    TrashbinIcon,
    UploadIcon,
    UsersIcon,
    ViewCardsIcon,
    ViewGalleryIcon,
    ViewListIcon,
} from 'reactApp/ui/VKUIIcons';
import { getSize } from 'reactApp/utils/sizeHelper';

import styles from './Toolbar.css';

export const toolbarItems: Pick<Record<EActions, TToolbarItem>, TToolbarItemsKeys> = {
    [EActions.upload]: {
        id: EActions.upload,
        text: 'Загрузить',
        icon: <Icon20CloudArrowUpOutline data-qa-id="upload" />,
        onClick: toolbarActions.upload,
    },
    [EActions.create]: {
        id: EActions.create,
        text: 'Создать',
        icon: <Icon20AddCircleOutline data-qa-id="create" />,
    },
    [EActions.selectAll]: {
        id: EActions.selectAll,
        onClick: toolbarActions.selectAll,
    },
    [EActions.download]: {
        id: EActions.download,
        text: 'Скачать',
        icon: <DownloadIcon data-qa-id="download" />,
        onClick: toolbarActions.download,
    },
    [EActions.remove]: {
        id: EActions.remove,
        text: 'Удалить',
        icon: <TrashbinIcon data-qa-id="remove" />,
        onClick: toolbarActions.remove,
    },
    [EActions.publish]: {
        id: EActions.publish,
        text: 'Получить ссылку',
        icon: <LinkIcon data-qa-id="publish" />,
        onClick: () => toolbarActions.publish('toolbar'),
    },
    [EActions.publishAlbum]: {
        text: 'Поделиться',
        id: EActions.publishAlbum,
        icon: <ShareAlbumIcon data-qa-id="publish-album" />,
        onClick: toolbarActions.createAndPublishAlbum,
    },
    [EActions.addToAlbum]: {
        text: 'Добавить в альбом',
        id: EActions.addToAlbum,
        icon: <AddToAlbum data-qa-id="add-to-album" />,
        onClick: toolbarActions.addToAlbum,
    },
    [EActions.unpublish]: {
        id: EActions.unpublish,
        text: 'Закрыть доступ',
        icon: <LinkIcon data-qa-id="unPublish" />,
        onClick: toolbarActions.unPublish,
    },
    [EActions.share]: {
        id: EActions.share,
        text: 'Настроить доступ',
        icon: <ShareIcon data-qa-id="share" />,
        onClick: toolbarActions.share,
    },
    [EActions.addToFavorites]: {
        text: 'В избранные',
        id: EActions.addToFavorites,
        icon: <FavoriteIcon data-qa-id="addToFavorites" color={EColor.BLACK} />,
        onClick: toolbarActions.toggleFavorites,
    },
    [EActions.removeFromFavorites]: {
        id: EActions.removeFromFavorites,
        text: 'Из избранных',
        icon: <FavoriteIcon data-qa-id="removeFromFavorites" active color={EColor.BLACK} />,
        onClick: toolbarActions.toggleFavorites,
    },
    [EActions.view]: {
        id: EActions.view,
        text: 'Вид',
    },
    [EActions.sort]: {
        id: EActions.sort,
        icon: <SortIcon data-qa-id="sort" />,
    },
    [EActions.sendViaMail]: {
        id: EActions.sendViaMail,
        text: 'Переслать',
        icon: <MailIcon data-qa-id="sendViaMail" />,
        onClick: toolbarActions.forward,
    },
    [EActions.more]: {
        id: EActions.more,
        icon: <MoreHorizontalIcon data-qa-id="more" />,
        withoutDropdownIcon: true,
    },
    [EActions.restore]: {
        id: EActions.restore,
        text: 'Восстановить',
        icon: <RestoreIcon data-qa-id="restore" />,
        onClick: toolbarActions.restore,
    },
    [EActions.restoreAll]: {
        id: EActions.restoreAll,
        text: 'Восстановить всё',
        icon: <RestoreIcon data-qa-id="restoreAll" />,
        onClick: toolbarActions.restoreAll,
    },
    [EActions.clear]: {
        id: EActions.clear,
        icon: <TrashbinIcon data-qa-id="clear" />,
        text: 'Очистить корзину',
        onClick: toolbarActions.clear,
    },
    [EActions.mount]: {
        id: EActions.mount,
        icon: <UsersIcon data-qa-id="mount" />,
        text: 'Подключить к Облаку',
        onClick: toolbarActions.mount,
    },
    [EActions.clone]: {
        id: EActions.clone,
        icon: <UploadIcon data-qa-id="clone" />,
        text: 'Сохранить в Облако',
        onClick: (id?: string) => toolbarActions.clone({ id, source: 'toolbar' }),
    },
};

export const toolbarDropdownItems: Pick<Record<EActions, ListItem>, TToolbarDropdownItemsKeys> = {
    [EActions.copy]: {
        id: EActions.copy,
        text: 'Копировать',
        icon: <CopyIcon data-qa-id="copy" />,
        onClick: toolbarActions.copy,
    },
    [EActions.history]: {
        id: EActions.history,
        text: 'Посмотреть историю',
        icon: <HistoryIcon data-qa-id="history" />,
        onClick: toolbarActions.viewHistory,
    },
    [EActions.move]: {
        id: EActions.move,
        text: 'Переместить в папку',
        icon: <MoveIcon data-qa-id="move" />,
        onClick: toolbarActions.move,
    },
    [EActions.rename]: {
        id: EActions.rename,
        text: 'Переименовать',
        icon: <RenameIcon data-qa-id="rename" />,
        onClick: toolbarActions.rename,
    },
    [EActions.viewThumbs]: {
        id: EActions.viewThumbs,
        key: EViewMode.thumbs,
        text: 'Таблицей',
        icon: <ViewCardsIcon data-qa-id="viewThumbs" />,
        withStatus: true,
        onClick: () => toolbarActions.view('thumbs'),
    },
    [EActions.viewList]: {
        text: 'Списком',
        id: EActions.viewList,
        key: EViewMode.list,
        withStatus: true,
        icon: <ViewListIcon data-qa-id="viewList" />,
        onClick: () => toolbarActions.view('list'),
    },
    [EActions.viewGallery]: {
        id: EActions.viewGallery,
        key: EViewMode.gallery,
        text: 'Галереей',
        icon: <ViewGalleryIcon />,
        withStatus: true,
        onClick: () => toolbarActions.view('gallery'),
    },
    [EActions.sortName]: {
        id: EActions.sortName,
        text: 'По алфавиту',
        icon: <TextIcon data-qa-id="sortName" />,
        withStatus: true,
        onClick: (asc: boolean) => toolbarActions.sort(`name-${asc ? ESortOder.asc : ESortOder.desc}`),
    },
    [EActions.sortSize]: {
        id: EActions.sortSize,
        text: 'По размеру',
        icon: <CompareIcon data-qa-id="sortSize" />,
        withStatus: true,
        onClick: (asc: boolean) => toolbarActions.sort(`size-${asc ? ESortOder.asc : ESortOder.desc}`),
    },
    [EActions.sortDate]: {
        id: EActions.sortDate,
        text: 'По дате',
        icon: <CalendarIcon data-qa-id="sortDate" />,
        withStatus: true,
        onClick: (asc: boolean) => toolbarActions.sort(`mtime-${asc ? ESortOder.asc : ESortOder.desc}`),
    },
    [EActions.createFolder]: {
        id: EActions.createFolder,
        text: 'Папку',
        icon: <FolderAddIcon data-qa-id="createFolder" />,
        onClick: (): void => toolbarActions.create('folder', 'toolbar'),
        hotkeyLabel: 'Shift+F',
    },
    [EActions.createDoc]: {
        id: EActions.createDoc,
        text: 'Документ',
        icon: <FileAddIcon data-qa-id="createDocx" color="#005FF9" />,
        onClick: (): void => toolbarActions.create('docx', 'toolbar'),
        hotkeyLabel: 'Shift+T',
    },
    [EActions.createCell]: {
        id: EActions.createCell,
        text: 'Таблицу',
        icon: <FileAddIcon data-qa-id="createXlsx" color="#0DC268" />,
        onClick: (): void => toolbarActions.create('xlsx', 'toolbar'),
        hotkeyLabel: 'Shift+S',
    },
    [EActions.createPpt]: {
        id: EActions.createPpt,
        text: 'Презентацию',
        icon: <FileAddIcon data-qa-id="createPptx" color="#FF9E00" />,
        onClick: (): void => toolbarActions.create('pptx', 'toolbar'),
        hotkeyLabel: 'Shift+P',
    },
};

export const getViewItemByModes = (activeViewMode: EViewMode, viewModes: EViewMode[]): TToolbarItem => {
    const list = viewModes.map((viewMode) => ({
        ...((Object.values(toolbarDropdownItems).find((item) => item.key === viewMode) ?? {}) as ListItem),
        active: activeViewMode === viewMode,
    }));

    return {
        ...toolbarItems[EActions.view],
        dropdownPosition: EDropdownPosition.right,
        list,
    };
};

export const getViewItem = ({ viewMode, isGalleryAvailable }): TToolbarItem => {
    const viewItem: TToolbarItem = {
        ...toolbarItems[EActions.view],
        dropdownPosition: EDropdownPosition.right,
        list: [
            {
                ...toolbarDropdownItems[EActions.viewList],
                active: viewMode === EViewMode.list,
            },
            {
                ...toolbarDropdownItems[EActions.viewThumbs],
                active: viewMode === EViewMode.thumbs,
            },
        ],
    };

    if (isGalleryAvailable) {
        viewItem.list?.push({
            ...toolbarDropdownItems[EActions.viewGallery],
            active: viewMode === EViewMode.gallery,
        });
    }

    return viewItem;
};

export const getSortItem = ({ sortType, sortOrder }): TToolbarItem => {
    const asc = sortOrder === ESortOder.asc;
    const newOrder = !asc ? 'asc' : 'desc';

    return {
        ...toolbarItems[EActions.sort],
        dropdownPosition: EDropdownPosition.right,
        list: [
            {
                ...toolbarDropdownItems[EActions.sortName],
                active: sortType === ESortTypes.name,
                onClick: () => toolbarActions.sort(`name-${sortType === 'name' ? newOrder : sortOrder}`),
                asc,
            },
            {
                ...toolbarDropdownItems[EActions.sortSize],
                active: sortType === ESortTypes.size,
                onClick: () => toolbarActions.sort(`size-${sortType === 'size' ? newOrder : sortOrder}`),
                asc,
            },
            {
                ...toolbarDropdownItems[EActions.sortDate],
                active: sortType === ESortTypes.mtime,
                onClick: () => toolbarActions.sort(`mtime-${sortType === 'mtime' ? newOrder : sortOrder}`),
                asc,
            },
        ],
    };
};

export const getEditorsItem = (availableCreateEditors: CreatorsDescRecord[]) =>
    availableCreateEditors.reduce((acc, cur) => {
        switch (cur.name) {
            case 'docx':
                return [...acc, toolbarDropdownItems[EActions.createDoc]];
            case 'xlsx':
                return [...acc, toolbarDropdownItems[EActions.createCell]];
            case 'pptx':
                return [...acc, toolbarDropdownItems[EActions.createPpt]];
            default:
                return acc;
        }
    }, [] as ListItem[]);

export const getDownloadItem = (
    items: CloudItem[],
    filesWithFaces: PublicFile[],
    currentFolder: CloudItem,
    isRoot?: boolean
): TToolbarItem => {
    const itemsSize = getDownloadItemSize(items);
    const itemsFacesSize = getDownloadItemSize(filesWithFaces);
    const folderSize =
        Boolean(currentFolder) &&
        ('sizeWithoutRestricted' in currentFolder ? currentFolder.sizeWithoutRestricted : 'size' in currentFolder && currentFolder.size);

    const itemsCount = items.length;

    let hintText = '';
    let text = 'Скачать';

    if (itemsCount && itemsSize) {
        text = `Скачать ${itemsSize}`;
        hintText = itemsSize;
    } else if (itemsFacesSize) {
        text = `Скачать ${itemsFacesSize}`;
        hintText = itemsFacesSize;
    } else if (folderSize) {
        const size = isRoot ? '' : ` ${getSize(folderSize)}`;
        text = `Скачать${size}`;
        hintText = getSize(folderSize);
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const ext = items[0]?.ext;

    if (itemsCount === 1 && ext && downloadAsJpeg.isAvailable({ ext, storage: items[0].storage })) {
        return {
            ...toolbarItems[EActions.download],
            id: 'downloadAsJpeg',
            text,
            openDropdownByArrow: true,
            dropdownPosition: EDropdownPosition.right,
            list: downloadAsJpeg.getDropdownItems({ itemOrId: items[0], gaSuffix: 'toolbar', ext }),
            dropdownTheme: DropdownTheme.jpeg,
        };
    }
    return { ...toolbarItems[EActions.download], text, hint: hintText };
};

export const getSelectItem = ({ itemsCount, itemsAllCount, allSelected }): TToolbarItem => {
    const text = allSelected && itemsCount ? 'Снять выделение' : 'Выделить все';
    const itemsCountText = itemsAllCount ? `${itemsCount} из ${itemsAllCount}` : itemsCount;

    return {
        ...toolbarItems[EActions.selectAll],
        icon: itemsCount ? (
            <CheckCircleIcon className={styles.selectIcon} data-qa-id="selectAll" />
        ) : (
            <CheckCircleOutlineIcon data-qa-id="selectAll" />
        ),
        text: (
            <>
                {Boolean(itemsCount) && <span className={styles.count}>{itemsCountText}</span>}
                {text}
            </>
        ),
    };
};

export const getCloneItem = (showTooltip: boolean) => {
    return {
        ...toolbarItems[EActions.clone],
        tooltipId: showTooltip ? EToolbarItemTooltip.publicCreateCloud : undefined,
    };
};
